// HelpAssistant/useAppHeaderObstruction.js
//
// fix-help-fab-covers-account-menu-1024w-short
//
// How tall the top app-chrome band is, so the FAB can never be clamped ONTO it.
//
// WHY this exists as its own input rather than "just don't have that bug":
// every vertical obstruction the FAB knows about (the consent banner, and the
// builder rail before it was corrected to a horizontal inset) pushes the FAB
// UP, and the top of the viewport is exactly where the app header lives. So
// "clear the obstruction" and "do not eat the header's controls" are one
// constraint seen from two ends, and the FAB previously only had the bottom
// end of it. MEASURED live on web-dev.iyuga.net/builder, uid 12675, bundle
// main.5419c4a9.js, 2026-09-07, at 1024x780 AND 1024x768:
//   header  0,0 - 1024,60     FAB (clamped up by the rail) 944,8 - 1000,64
//   -> 2912px^2 on the header, 2492.33px^2 (87%) on [aria-label="Account menu"]
//      (947.53,4.5 56x51), and document.elementFromPoint at the account
//      button's own centre returned the FAB's help-icon <svg>. Clicking the
//      avatar opened the Help Assistant; Logout, Settings, Media Library,
//      Analytics, Themes and Add account were all unreachable.
//   POSITIVE CONTROL in the same runs, 1440x900: FAB 1360,820, header overlap
//   0, elementFromPoint returned the account <img>, the real menu opened.
//
// The direct cause (the rail being modelled as a bottom obstruction) is fixed
// in fabPosition.js/useBuilderRailInset.js. This hook closes the CLASS: with a
// header floor in the clamp, no present or future vertical obstruction can put
// the FAB on the app header again, whatever pushes it.
//
// Same shape as useConsentObstruction.js: the height is genuinely dynamic
// (measured 60px @1024 and 72px @1440 on the builder) so it is read off the
// real element rather than hard-coded per breakpoint.

import { useEffect, useState } from "react";

/**
 * The app's top chrome, in priority order:
 *  - `[data-testid="builder-app-header"]` — the Segment Builder's own header
 *    (the builder does NOT render the shared Navbar; MEASURED 2026-09-07, a
 *    `header,[role=banner]` census on /builder returned zero elements).
 *  - `header` / `[role="banner"]` — the shared Navbar on every other route
 *    (components/Navbar renders `Styled.Container as="header"`).
 */
export const APP_HEADER_SELECTOR =
  '[data-testid="builder-app-header"], header, [role="banner"]';

/** Largest viewport edge inset a band may start at and still count as "the header". */
const HEADER_TOP_TOLERANCE = 1;

/** A band taller than this is a page section, not app chrome — ignore it. */
export const MAX_HEADER_HEIGHT = 160;

/**
 * Measured height of the top app header, 0 when there is none on screen.
 * Only counts an element that actually sits AT the top of the viewport and
 * spans most of its width — a `header` scrolled out of view, or a card's own
 * `<header>`, must never reserve space at the top of the screen. Never throws.
 */
export const measureAppHeaderHeight = (doc, viewportWidth) => {
  try {
    const root = doc || (typeof document !== "undefined" ? document : null);
    if (!root || typeof root.querySelectorAll !== "function") return 0;
    const vw =
      viewportWidth ?? (typeof window !== "undefined" ? window.innerWidth : 0);
    if (!vw) return 0;
    const bands = Array.from(root.querySelectorAll(APP_HEADER_SELECTOR))
      .filter((el) => typeof el.getBoundingClientRect === "function")
      .map((el) => el.getBoundingClientRect())
      .filter(
        (r) =>
          r.top <= HEADER_TOP_TOLERANCE &&
          r.height > 0 &&
          r.height <= MAX_HEADER_HEIGHT &&
          r.width >= vw * 0.9
      );
    if (!bands.length) return 0;
    // The band's BOTTOM edge is what the FAB must stay below, and a rect whose
    // top is slightly negative (a header scrolled 1px) still ends where it ends.
    return Math.max(0, Math.ceil(Math.max(...bands.map((r) => r.bottom))));
  } catch (e) {
    return 0;
  }
};

/**
 * Live height (px, 0 when absent) of the top app header band. The FAB is never
 * clamped onto it — see clampFabPosition's `topExclusionHeight`.
 */
export default function useAppHeaderObstruction() {
  const [height, setHeight] = useState(() => measureAppHeaderHeight());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const measure = () =>
      setHeight((prev) => {
        const next = measureAppHeaderHeight();
        return next === prev ? prev : next;
      });
    // Measure now, and once more after paint: the header remounts on route
    // change (builder header <-> shared Navbar), so the first synchronous read
    // can miss it.
    measure();
    const raf =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(measure)
        : null;
    // The header reflows between breakpoints (60px @1024 -> 72px @1440).
    window.addEventListener("resize", measure);
    const observer =
      typeof MutationObserver !== "undefined" ? new MutationObserver(measure) : null;
    if (observer && document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
    return () => {
      if (raf && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(raf);
      }
      window.removeEventListener("resize", measure);
      if (observer) observer.disconnect();
    };
  }, []);

  return height;
}
